import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Mail, Lock, User } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Checkbox } from "../components/ui/checkbox";
import { Logo } from "../components/Logo";
import { formatApiError } from "../lib/api";
import { toast } from "sonner";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function googleLogin() {
  const redirectUrl = window.location.origin + "/tableau-de-bord";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}

export default function Register() {
  const [form, setForm] = useState({ display_name: "", email: "", password: "" });
  const [age, setAge] = useState(false);
  const [busy, setBusy] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!age) { toast.error("Vous devez confirmer être majeur(e)."); return; }
    setBusy(true);
    try {
      await register({ ...form, age_confirmed: true });
      toast.success("Compte créé ! 30 crédits de bienvenue offerts.");
      navigate("/tableau-de-bord");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <div className="mx-auto flex max-w-md flex-col items-center px-4 py-16">
      <Logo />
      <div className="glass mt-8 w-full rounded-3xl border border-celeste-or/15 p-7">
        <h1 className="font-serif text-2xl text-celeste-ivoire">Créer un compte</h1>
        <p className="mt-1 text-sm text-celeste-lilas/70">Rejoignez Guidance Céleste Live (réservé aux 18+).</p>

        <form onSubmit={submit} className="mt-6 space-y-4">
          <div className="relative">
            <User className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-celeste-lilas/60" />
            <Input data-testid="register-name" required value={form.display_name} onChange={set("display_name")}
              placeholder="Votre prénom" className="bg-celeste-night/50 border-celeste-violet/30 pl-9 text-celeste-ivoire" />
          </div>
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-celeste-lilas/60" />
            <Input data-testid="register-email" type="email" required value={form.email} onChange={set("email")}
              placeholder="Adresse e-mail" className="bg-celeste-night/50 border-celeste-violet/30 pl-9 text-celeste-ivoire" />
          </div>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-celeste-lilas/60" />
            <Input data-testid="register-password" type="password" required minLength={6} value={form.password} onChange={set("password")}
              placeholder="Mot de passe (6 caractères min.)" className="bg-celeste-night/50 border-celeste-violet/30 pl-9 text-celeste-ivoire" />
          </div>
          <label className="flex items-start gap-3 rounded-xl border border-celeste-violet/30 bg-celeste-night/40 p-3 text-sm text-celeste-lilas cursor-pointer">
            <Checkbox data-testid="register-age" checked={age} onCheckedChange={(v) => setAge(!!v)} className="mt-0.5 border-celeste-or data-[state=checked]:bg-celeste-or" />
            Je certifie avoir 18 ans ou plus et j'accepte les CGU et la politique de confidentialité.
          </label>
          <Button data-testid="register-submit" type="submit" disabled={busy} className="w-full gold-btn font-semibold">
            {busy ? "Création…" : "Créer mon compte"}
          </Button>
        </form>

        <div className="my-5 flex items-center gap-3 text-xs text-celeste-lilas/50">
          <div className="h-px flex-1 bg-celeste-or/15" /> ou <div className="h-px flex-1 bg-celeste-or/15" />
        </div>
        <Button data-testid="google-register-btn" onClick={googleLogin} variant="ghost"
          className="w-full border border-celeste-violet/30 text-celeste-ivoire hover:bg-celeste-violet/15">
          S'inscrire avec Google
        </Button>

        <p className="mt-6 text-center text-sm text-celeste-lilas/70">
          Déjà inscrit ? <Link to="/connexion" className="text-celeste-or hover:underline">Se connecter</Link>
        </p>
      </div>
    </div>
  );
}
